import {
  Checkbox,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
  Switch,
  Textarea,
  tokens,
} from "@fluentui/react-components";
import type { AttributeDefinition } from "../api/catalogue";

const useStyles = makeStyles({
  // Checkboxes in a grid so a long list of embellishments does not become a long scroll on a
  // phone. Two columns on a narrow screen, more as it widens.
  checks: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))",
    gap: tokens.spacingVerticalXXS,
  },
});

export type AttributeValue = { value: string | null; selectedValues: string[] };

type Props = {
  definition: AttributeDefinition;
  value: AttributeValue;
  onChange: (next: AttributeValue) => void;
  error?: string;
};

/**
 * Renders one tenant-defined field.
 *
 * The control is chosen from the field's declared type, not from its name — which is what lets an
 * owner add "Neckline" in settings and get a working dropdown without anyone touching this file.
 *
 * Select becomes a dropdown and MultiSelect becomes checkboxes, both deliberately: picking from a
 * list is faster than typing on a phone, and it keeps the data clean enough to filter on later.
 */
export function AttributeField({ definition, value, onChange, error }: Props) {
  const styles = useStyles();

  const field = (children: React.ReactNode) => (
    <Field
      label={definition.name}
      required={definition.isRequired}
      hint={definition.hint ?? undefined}
      validationMessage={error}
      validationState={error ? "error" : "none"}
    >
      {children}
    </Field>
  );

  switch (definition.dataType) {
    case "Select":
      return field(
        <Dropdown
          placeholder="Choose one"
          value={definition.options.find((o) => o.value === value.value)?.label ?? ""}
          selectedOptions={value.value ? [value.value] : []}
          onOptionSelect={(_, data) =>
            onChange({ value: data.optionValue ?? null, selectedValues: [] })
          }
        >
          {definition.options.map((option) => (
            <Option key={option.value} value={option.value} text={option.label}>
              {option.label}
            </Option>
          ))}
        </Dropdown>,
      );

    case "MultiSelect":
      return field(
        <div className={styles.checks}>
          {definition.options.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              checked={value.selectedValues.includes(option.value)}
              onChange={(_, data) =>
                onChange({
                  value: null,
                  selectedValues: data.checked
                    ? [...value.selectedValues, option.value]
                    : value.selectedValues.filter((v) => v !== option.value),
                })
              }
            />
          ))}
        </div>,
      );

    case "Boolean":
      return field(
        <Switch
          checked={value.value === "true"}
          onChange={(_, data) => onChange({ value: String(data.checked), selectedValues: [] })}
        />,
      );

    case "LongText":
      return field(
        <Textarea
          value={value.value ?? ""}
          rows={3}
          onChange={(_, data) => onChange({ value: data.value, selectedValues: [] })}
        />,
      );

    case "Number":
      return field(
        <Input
          type="number"
          inputMode="decimal"
          value={value.value ?? ""}
          onChange={(_, data) => onChange({ value: data.value, selectedValues: [] })}
        />,
      );

    case "Date":
      return field(
        <Input
          type="date"
          value={value.value ?? ""}
          onChange={(_, data) => onChange({ value: data.value, selectedValues: [] })}
        />,
      );

    default:
      return field(
        <Input
          value={value.value ?? ""}
          onChange={(_, data) => onChange({ value: data.value, selectedValues: [] })}
        />,
      );
  }
}
